<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import {
	Card,
	Label,
	Input,
	Button,
	Icon,
	Select,
	ProfilePicture,
	InputPrice,
} from "@components";

import { getPrice } from "@utils";
import moment from "moment";

// Route.
import { useRoute, useRouter } from "vue-router";
const route = useRoute();
const router = useRouter();

import { useTransaction } from "@store/transaction.js";
const $transaction = useTransaction();

import { useCustomer } from "@store/customer.js";
const $customer = useCustomer();

import { useService } from "@store/service.js";
const $service = useService();

import { useSettings } from "@store/settings.js";
const $settings = useSettings();

// Options.
// const $settings = window.prebook_admin.settings || {}
const $options = prebook_admin.options || {};

const transactionId = computed(() => {
	return route.params.transactionId;
});

// State.
const state = reactive({
	// anotherPayment: false,
});

const transaction = computed(() => {
	return $transaction.getTransaction || {};
});

const save = async () => {
	state.loading = true;
	const response = await transaction.value.update(
		transaction.value,
		transaction.value.transaction_id
	);
	console.log(response);
	state.loading = false;

	if (response.success) {
		Toast.success(`${$settings.labels.payment?.singular} updated successfully`);

		// back to list page [issue #82]
		router.push({ name: "transaction.list" });
	} else {
		Toast.error(response.message);
	}
};

const availablePaymentMethods = computed(() => {
	// Filter only available payment methods
	let enabledPaymentMethods = $settings.payment.methods.filter(
		(method) => method.enabled
	);
	return enabledPaymentMethods.map((method) => {
		return {
			label: method.name,
			value: String(method.name).toLowerCase(),
		};
	});
});

onMounted(async () => {
	Log("appointmentId", route.params.appointmentId);
	if (route.params.appointmentId) {
		Log("appointmentId", route.params.appointmentId);
	}

	state.loading = true;
	$customer.fetchCustomers();
	await $transaction.fetchTransaction(transactionId.value);
	Log("TRANSACTION", transaction.value);
	state.loading = false;

	// Set default payment method
	transaction.value.payment_method =
		transaction.value.payment_method ||
		$settings.payment.default_payment_method ||
		"cash";
});
</script>

<template>
	<div class="md:max-w-[736px] m-auto">
		<!-- Header -->
		<div class="flex items-center justify-between mb-7">
			<div>
				<p class="text-[#344054] text-sm font-semibold mb-2">{{ $settings.labels.payment?.singular }} details</p>
				<h2 class="text-[#101828] text-3xl font-semibold">{{ $settings.labels.payment?.singular }} ID #{{ transactionId }}</h2>
			</div>
			<!-- <div class="text-sm font-medium py-0.5 px-2.5 rounded-2xl capitalize" :class="[transaction.payment_status == 'publish' ? 'text-[#027A48] bg-[#ECFDF3]' : 'text-[#7a1202] bg-[#fdecec]']">
                {{ transaction.payment_status == 'publish' ? 'Paid' : transaction.payment_status }}
			</div>-->
			<div class="text-sm font-medium py-0.5 px-2.5 rounded-2xl capitalize" :class="{
                'text-[#027A48] bg-[#ECFDF3]': transaction.payment_status == 'paid',
                'text-[#B84646] bg-[#FEE2E2]': transaction.payment_status !== 'paid'
            }">{{ transaction.payment_status || 'unpaid' }}</div>
		</div>

		<!-- Appointment details -->
		<div class="p-6 border border-[#EAECF0] rounded-xl mb-6">
			<!-- date -->
			<div class="flex items-center justify-between">
				<div class="flex items-center gap-3">
					<Icon name="calendar" size="24px" class="text-[#475467]" />
					<span class="text-[#101828] text-sm font-medium">Date</span>
				</div>
				<div class="text-[#101828] text-sm font-semibold">{{ moment(transaction.created_at, 'YYYY-MM-DD hh:mm:ss').format('MMMM DD, YYYY') }}</div>
			</div>
			<div class="w-full h-[1px] bg-[#EAECF0] my-3"></div>

			<!-- service -->
			<div class="flex items-center justify-between">
				<div class="flex items-center gap-3">
					<Icon name="hash02" size="24px" class="text-[#475467]" />
					<span class="text-[#101828] text-sm font-medium capitalize">{{ $settings.labels.service?.singular }}</span>
				</div>
				<div class="text-[#101828] text-sm font-semibold truncate max-w-[60%]">
					<!-- {{ $service.getServiceById(transaction.service_id) }} -->
					{{ transaction.service_name }}
				</div>
			</div>
			<div class="w-full h-[1px] bg-[#EAECF0] my-3"></div>

			<!-- customer -->
			<div class="flex items-center justify-between">
				<div class="flex items-center gap-3">
					<Icon name="user01" size="24px" class="text-[#475467]" />
					<span class="text-[#101828] text-sm font-medium capitalize">{{ $settings.labels.customer?.singular }}</span>
				</div>
				<div class="flex gap-2">
					<ProfilePicture :name="$customer.findCustomerById(transaction.customer_id)?.name" :img="$customer.findCustomerById(transaction.customer_id)?.meta?.profile_image_url" :bgColor="$customer.findCustomerById(transaction.customer_id)?.meta?.color" class="w-5 h-5" textClass="!text-xs" />
					<div class="text-[#101828] text-sm font-semibold truncate">{{ $customer.findCustomerById(transaction.customer_id).name || '-' }}</div>
				</div>
			</div>
			<div class="w-full h-[1px] bg-[#EAECF0] my-3"></div>

			<!-- time -->
			<div class="flex items-center justify-between">
				<div class="flex items-center gap-3">
					<Icon name="clock" size="24px" class="text-[#475467]" />
					<span class="text-[#101828] text-sm font-medium">Local Time</span>
				</div>
				<div class="text-[#101828] text-sm font-semibold">{{ moment(transaction.created_at, 'YYYY-MM-DD hh:mm:ss').format('hh:mm A') }}</div>
			</div>
			<div class="w-full h-[1px] bg-[#EAECF0] my-3"></div>

			<!-- location -->
			<!-- <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                    <Icon name="markerPin01" size="24px" class="text-[#475467]" />
                    <span class="text-[#101828] text-sm font-medium">Location</span>
                </div>
                <div class="text-[#101828] text-sm font-semibold">
                    {{ transaction.booking_date }}
                </div>
            </div>
			<div class="w-full h-[1px] bg-[#EAECF0] my-3"></div>-->

			<!-- transaction method -->
			<div class="flex items-center justify-between">
				<div class="flex items-center gap-3">
					<Icon name="bankNote01" size="24px" class="text-[#475467]" />
					<span class="text-[#101828] text-sm font-medium capitalize">{{ $settings.labels.payment?.singular }} Method</span>
				</div>
				<div class="text-[#101828] text-sm font-semibold capitalize">{{ String(transaction.payment_method).split('_').join(' ') }}</div>
			</div>
		</div>

		<!-- Appointment calculation -->
		<div class="p-6 border border-[#EAECF0] rounded-xl">
			<!-- <div class="flex justify-between items-center text-[#344054] font-normal">
                <div class="text-sm">Dental Examination</div>
                <div class="text-base">
                    {{ getPrice(transaction.payment_amount) }}
                </div>
            </div>

			<div class="w-full h-[1px] bg-[#EAECF0] my-3"></div>-->

			<div class="flex justify-between items-center text-[#101828] font-semibold">
				<div class="text-sm">Total</div>
				<div class="text-base">{{ getPrice(transaction.payment_amount) }}</div>
			</div>
		</div>

		<!-- transaction method -->
		<!-- <div class="p-6 border border-[#EAECF0] rounded-xl mt-5">
            <div class="text-[#101828] text-lg font-semibold mb-5">Payment and method</div>

            <div class="flex flex-col gap-4 mb-5 md:flex-row">
                <div class="w-full">
                    <Label>Paid</Label>
                    <Input inputContainerClass="py-2 px-3" :iconLeft="$settings.payment.currency_symbol" type="number" class="my-1.5"></Input>
                </div>
                <div class="w-full">
                    <Label>Due</Label>
                    <Input inputContainerClass="py-2 px-3" :iconLeft="$settings.payment.currency_symbol" type="number" class="my-1.5"></Input>
                </div>
            </div>

            <div class="w-full mb-5">
                <Label>Payment Method</Label>
                <Select selectionContainer="!py-2 !px-3" :options="$options.payment_methods" class="my-1.5"></Select>
            </div>

            <div class="w-full">
                <Label>Update Reason</Label>
                <Input inputContainerClass="py-2 px-3" type="text" class="my-1.5"></Input>
            </div>
        </div>

        <Button v-if="!state.anotherPayment" @click="state.anotherPayment = true" size="xl" type="primary" radius="square" classButton="w-full mt-12">
            Add another payment
		</Button>-->

		<!-- another payment -->
		<div class="p-6 border border-[#EAECF0] rounded-xl mt-5">
			<div class="text-[#101828] text-lg font-semibold mb-5 capitalize">{{ $settings.labels.payment?.singular }} and method</div>

			<div class="flex flex-col gap-4 md:flex-row">
				<div class="w-full">
					<Label>Amount</Label>
					<!-- <Input v-model="transaction.payment_amount" inputContainerClass="py-2 px-3" :iconLeft="$settings.payment.currency_symbol" type="number" class="my-1.5"></Input> -->
					<InputPrice v-model="transaction.payment_amount" class="my-1.5" inputContainerClass="!py-2 !px-3" />
				</div>
				<div class="w-full">
					<Label class="capitalize">{{ $settings.labels.payment?.singular }} Method</Label>
					<!-- {{ availablePaymentMethods }} -->
					<Select v-model="transaction.payment_method" selectionContainer="py-[8px] px-[12px]" :options="availablePaymentMethods" class="my-1.5"></Select>
				</div>
			</div>
			<!-- <div class="w-full">
                <Label>Update Reason <span class="text-[#D92D20]">*</span></Label>
                <Input inputContainerClass="py-2 px-3" type="text" class="my-1.5" placeholder="Ex: Customer paid due amount."></Input>
			</div>-->

			<Button @click="save" size="xl" type="primary" radius="square" classButton="w-full md:mt-5 justify-between">
				<div class="w-[95%]">Update</div>
			</Button>
			<!-- <div class="flex flex-col items-center gap-3 mt-5 md:gap-10 md:flex-row">
                <Button  size="xl" type="secondary_gray" radius="square" classButton="w-full md:!w-[180px] md:mt-10 justify-between" @click.prevent="$router.push({ query: { tab: 'details' } })">
                    <div class="w-[70%]">Cancel</div>
                    <template #before>
                        <Icon name="arrowNarrowLeft" size="20px"></Icon>
                    </template>

                </Button>
			</div>-->
		</div>
	</div>
</template>
